import { useNavigate } from "react-router-dom";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";
import { CustomEventType } from "@/enum/custom-event.enum";
import { ScrapeLevel } from "@/enum/job.enum";
import { ListSourceType } from "@/enum/list.enum";
import { useCreateListMutation } from "@/pages/Private/redux/list/list.api";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { CatalogTile } from "../config/tileCatalog";
import { SOURCE_MODE_PARAM } from "../utils/sourceMode";

export const useCreateListFromTile = () => {
	const [createList] = useCreateListMutation();
	const navigate = useNavigate();
	const { run, isRunning } = useSingleFlight();

	const createTile = async (tile: CatalogTile) => {
		let entityType: CsvEnrichmentType | CustomEventType | null = null;
		let scrapeLevel: ScrapeLevel | null = null;

		if (
			[ListSourceType.MAPS, ListSourceType.JOBS, ListSourceType.PARALLEL_AI].includes(tile.listType)
		) {
			scrapeLevel = tile.subType as ScrapeLevel;
		} else if (
			[ListSourceType.CSV_ENRICHMENTS, ListSourceType.JOB_CHANGE].includes(tile.listType)
		) {
			entityType = tile.subType as CsvEnrichmentType;
		} else if (tile.listType === ListSourceType.CUSTOM_EVENTS) {
			entityType = tile.subType as CustomEventType;
		}

		const createdList = await createList({
			sourceType: tile.listType,
			entityType,
			scrapeLevel,
			isNewCreationProcess: true,
		}).unwrap();

		if (createdList.sourceType === ListSourceType.EMPTY) {
			navigate(`/app/lists/detail/${createdList.id}`);

			return;
		}

		// Deep-link the picked sub-type so the source form pre-selects that mode.
		const modeQuery = tile.mode ? `?${SOURCE_MODE_PARAM}=${tile.mode}` : "";

		navigate(`/app/lists/create/source/${createdList.id}${modeQuery}`);
	};

	// A double click on a catalog tile used to POST /list twice and leave two lists behind
	// (PREC-4554). A second click — on the same tile or another one — is dropped until this one
	// has created its list and navigated away.
	const createFromTile = (tile: CatalogTile) => run(() => createTile(tile));

	return { createFromTile, isLoading: isRunning };
};
